<template>
  <div class="galeria-page" data-testid="galeria-page">
    <box>
      <template #header>
        <h4 class="box-title">
          <i class="pi pi-image"></i>
          {{ t('Imagens dos serviços') }}
        </h4>
        <div v-if="isAdmin" class="thumbnail-controls">
          <Button
            v-if="!thumbnailProgress.isProcessing"
            :label="t('Gerar Thumbnails')"
            icon="pi pi-image"
            size="small"
            data-testid="galeria-generate-thumbnails-button"
            @click="handleGenerateThumbnails"
          />
          <Button
            v-else
            :label="t('Cancelar')"
            icon="pi pi-times"
            size="small"
            severity="danger"
            data-testid="galeria-cancel-thumbnails-button"
            @click="handleCancelGeneration"
          />
        </div>
      </template>

      <!-- Progress Panel -->
      <div v-if="thumbnailProgress.isProcessing || showProgressSummary" class="thumbnail-progress-panel">
        <div class="progress-header">
          <h5>
            <i :class="thumbnailProgress.isProcessing ? 'pi pi-spin pi-spinner' : 'pi pi-check-circle'"></i>
            {{ thumbnailProgress.isProcessing ? t('Gerando thumbnails...') : t('Geração concluída') }}
          </h5>
          <Button
            v-if="showProgressSummary && !thumbnailProgress.isProcessing"
            icon="pi pi-times"
            text
            rounded
            size="small"
            data-testid="galeria-close-progress-button"
            @click="handleCloseProgress"
          />
        </div>

        <!-- Progress Bar -->
        <div class="progress-bar-container">
          <progress-bar
            :value="progressPercentage"
            :showValue="false"
          />
          <span class="progress-text">
            {{ thumbnailProgress.current }} / {{ thumbnailProgress.total }}
            ({{ progressPercentage }}%)
          </span>
        </div>

        <!-- Current Image -->
        <div v-if="thumbnailProgress.isProcessing && thumbnailProgress.currentImageName" class="current-image">
          <i class="pi pi-image"></i>
          <span>{{ t('Processando') }}: {{ truncateFileName(thumbnailProgress.currentImageName) }}</span>
        </div>

        <!-- Statistics -->
        <div class="progress-stats">
          <div class="stat-item stat-created">
            <i class="pi pi-check"></i>
            <span>{{ t('Criados') }}: {{ thumbnailProgress.created }}</span>
          </div>
          <div class="stat-item stat-existing">
            <i class="pi pi-minus-circle"></i>
            <span>{{ t('Existentes') }}: {{ thumbnailProgress.alreadyExist }}</span>
          </div>
          <div v-if="thumbnailProgress.errors > 0" class="stat-item stat-errors">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ t('Erros') }}: {{ thumbnailProgress.errors }}</span>
          </div>
        </div>

        <!-- Error Details (collapsible) -->
        <div v-if="thumbnailProgress.errorDetails.length > 0" class="error-details">
          <Button
            :label="showErrors ? t('Ocultar erros') : t('Ver erros')"
            icon="pi pi-exclamation-triangle"
            text
            size="small"
            severity="danger"
            data-testid="galeria-toggle-errors-button"
            @click="showErrors = !showErrors"
          />
          <ul v-if="showErrors" class="error-list">
            <li v-for="(error, index) in thumbnailProgress.errorDetails" :key="index">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>

      <GaleriaTable
        @view-gallery="handleViewGallery"
      />
    </box>

    <GaleriaImageGallery
      v-if="showGallery && selectedCompromissoServicoId !== null"
      :compromisso-servico-id="selectedCompromissoServicoId"
      data-testid="galeria-image-gallery-container"
      @close="handleCloseGallery"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import GaleriaTable from './GaleriaTable.vue';
import GaleriaImageGallery from './GaleriaImageGallery.vue';
import { useGaleria } from '@/composables/useGaleria';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface ThumbnailStats {
  thumbnails_created: number;
  errors: number;
  was_cancelled?: boolean;
}

const { t } = useI18n();

const {
  thumbnailProgress,
  generateMissingThumbnailsBatch,
  cancelThumbnailGeneration,
  resetThumbnailProgress
} = useGaleria();
const { confirmAction } = useConfirmAction();

const showGallery = ref<boolean>(false);
const selectedCompromissoServicoId = ref<number | string | null>(null);
const showProgressSummary = ref<boolean>(false);
const showErrors = ref<boolean>(false);

const isAdmin = computed<boolean>(() => {
  return permissionsService.isUserInGroups(['Full']);
});

const progressPercentage = computed<number>(() => {
  if (thumbnailProgress.value.total === 0) return 0;
  return Math.round((thumbnailProgress.value.current / thumbnailProgress.value.total) * 100);
});

const handleViewGallery = ({ compromissoServicoId }: { compromissoServicoId: string | null }): void => {
  log('GaleriaPage', 'handleViewGallery', 'Opening image gallery', { compromissoServicoId });
  if (!compromissoServicoId) return;
  selectedCompromissoServicoId.value = compromissoServicoId;
  showGallery.value = true;
};

const handleCloseGallery = (): void => {
  log('GaleriaPage', 'handleCloseGallery', 'Closing image gallery');
  showGallery.value = false;
  selectedCompromissoServicoId.value = null;
};

const truncateFileName = (fileName: string | null | undefined): string => {
  if (!fileName) return '';
  if (fileName.length <= 40) return fileName;
  return fileName.substring(0, 20) + '...' + fileName.substring(fileName.length - 15);
};

const handleGenerateThumbnails = (): void => {
  log('GaleriaPage', 'handleGenerateThumbnails', 'Starting thumbnail generation with batch processing');

  confirmAction({
    message: t('Deseja gerar thumbnails para todas as imagens que ainda não possuem?'),
    header: t('Confirmação'),
    onAccept: async () => {
      showProgressSummary.value = true;
      showErrors.value = false;

      try {
        const stats: ThumbnailStats = await generateMissingThumbnailsBatch();
        log('GaleriaPage', 'handleGenerateThumbnails', 'Batch generation completed', stats);

        const detail = `${t('Criados')}: ${stats.thumbnails_created} | ${t('Erros')}: ${stats.errors}`;

        if (stats.was_cancelled) {
          eventBus.emit('globalMessage.warn', `${t('Geração cancelada')} — ${detail}`);
        } else if (stats.errors > 0) {
          eventBus.emit('globalMessage.warn', `${t('Geração de thumbnails concluída!')} — ${detail}`);
        } else {
          eventBus.emit('globalMessage.success', `${t('Geração de thumbnails concluída!')} — ${detail}`);
        }

        eventBus.emit('galeria.refresh');
      } catch (error) {
        log('GaleriaPage', 'handleGenerateThumbnails', 'Error generating thumbnails', error);
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', `${t('Erro ao gerar thumbnails')}: ${message}`);
        showProgressSummary.value = false;
      }
    }
  });
};

const handleCancelGeneration = (): void => {
  log('GaleriaPage', 'handleCancelGeneration', 'Cancelling thumbnail generation');
  cancelThumbnailGeneration();
  eventBus.emit('globalMessage.warn', t('A geração será interrompida após a imagem atual.'));
};

const handleCloseProgress = (): void => {
  showProgressSummary.value = false;
  resetThumbnailProgress();
};

onMounted(() => {
  log('GaleriaPage', 'onMounted', 'Page mounted');
});
</script>

<style scoped>
.galeria-page {
  padding: 0;
}

.box-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.thumbnail-controls {
  display: flex;
  gap: 8px;
}

/* Progress Panel */
.thumbnail-progress-panel {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-header h5 i {
  color: #10b981;
}

.progress-header h5 .pi-spinner {
  color: #3b82f6;
}

.progress-bar-container {
  margin-bottom: 12px;
}

.progress-text {
  display: block;
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 6px;
}

.current-image {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #4b5563;
  padding: 8px 12px;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 12px;
}

.current-image i {
  color: #3b82f6;
}

.progress-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.stat-created {
  color: #059669;
}

.stat-created i {
  color: #10b981;
}

.stat-existing {
  color: #6b7280;
}

.stat-existing i {
  color: #9ca3af;
}

.stat-errors {
  color: #dc2626;
}

.stat-errors i {
  color: #ef4444;
}

.error-details {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #dee2e6;
}

.error-list {
  margin: 8px 0 0 0;
  padding: 8px 12px 8px 28px;
  background: #fef2f2;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #991b1b;
  max-height: 150px;
  overflow-y: auto;
}

.error-list li {
  margin-bottom: 4px;
}

.error-list li:last-child {
  margin-bottom: 0;
}

:deep(.pi-spin) {
  animation: pi-spin 1s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
